import { css } from '@emotion/css';
import { Button, message, Popover, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { modeTranslate, TranslateMode } from 'services/api/overseaTranslate';
import { ReactComponent as GoogleSvg } from 'views/Conversation/assets/google.svg';
import { ReactComponent as GptSvg } from 'views/Conversation/assets/gpt.svg';
import { ReactComponent as DeepseekSvg } from 'views/Conversation/assets/deepseek.svg';
import { ReactComponent as ChatPlusAISvg } from 'views/Conversation/assets/chatplusai.svg';
import { ReactComponent as HideTransSvg } from 'views/Conversation/assets/hide_translate.svg';
import { useTranslate } from '@tolgee/react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import Icon, { DownOutlined } from '@ant-design/icons';

function useModeTranslate(texts: string[]) {
  const { t } = useTranslate();

  const sysLang = useSystemLanguageValue();

  const [translateVisible, setTranslateVisible] = useState(false);

  const [translating, setTranslating] = useState(false);

  const [translateMode, setTranslateMode] = useState<TranslateMode>();

  const [result, setResult] = useState<Partial<Record<string, string>>>();

  const translate = useCallback(
    ({
      texts,
      sysLang,
      mode,
    }: {
      texts: string[];
      sysLang: General.SystemLanguage;
      mode: TranslateMode;
    }) => {
      if (!texts.length) {
        return;
      }

      setTranslateVisible(false);

      setTranslating(true);

      return modeTranslate(texts, sysLang, mode, { from: 'review' })
        .then((res) => {
          setResult(
            res.reduce<Partial<Record<string, string>>>((res, cur, index) => {
              res[texts[index]] = cur;
              return res;
            }, {}),
          );
        })
        .catch(() => {
          message.error(t('phrase.翻译失败'));
        })
        .finally(() => {
          setTranslating(false);
        });
    },
    [t],
  );

  useEffect(() => {
    if (translateMode && sysLang !== 'en-US') {
      translate({
        texts,
        sysLang,
        mode: translateMode,
      });
    }
  }, [translate, texts, sysLang, translateMode]);

  useEffect(() => {
    if (sysLang === 'en-US') {
      setResult(undefined);
      setTranslateMode(undefined);
    } else {
      setTranslateMode('CHATPLUSAI');
    }
  }, [sysLang]);

  const trigger = (
    <Popover
      visible={translateVisible && sysLang !== 'en-US'}
      onVisibleChange={setTranslateVisible}
      placement="bottom"
      align={{ offset: [0, -4] }}
      overlayClassName={styles.popover}
      content={
        <>
          {(
            [
              { title: 'Google', mode: 'GOOGLE', icon: GoogleSvg },
              { title: 'ChatGPT', mode: 'CHAT_GPT', icon: GptSvg },
              { title: 'DeepSeek', mode: 'DEEPSEEK', icon: DeepseekSvg },
              { title: 'ChatPlusAI', mode: 'CHATPLUSAI', icon: ChatPlusAISvg },
            ] as const
          ).map(({ title, mode, icon }) => {
            return (
              <Tooltip
                getTooltipContainer={(_) => _}
                title={
                  result
                    ? t('ViewsChat.使用x重新翻译', { x: title })
                    : t('ViewsChat.使用x翻译', { x: title })
                }
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setTranslateMode(mode);
                    translate({
                      texts,
                      sysLang,
                      mode,
                    });
                  }}
                >
                  <Icon component={icon} />
                </div>
              </Tooltip>
            );
          })}
          {result && (
            <Tooltip title={t('hide_translate')}>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setTranslateVisible(false);
                  setResult(undefined);
                  setTranslateMode(undefined);
                }}
              >
                <Icon component={HideTransSvg} />
              </div>
            </Tooltip>
          )}
        </>
      }
    >
      <Button type="link" loading={translating} disabled={sysLang === 'en-US'}>
        {result && translateMode && (
          <Icon
            component={
              {
                GOOGLE: GoogleSvg,
                CHAT_GPT: GptSvg,
                DEEPSEEK: DeepseekSvg,
                CHATPLUSAI: ChatPlusAISvg,
              }[translateMode]
            }
          />
        )}
        {t('phrase.翻译')}
        <DownOutlined />
      </Button>
    </Popover>
  );

  return {
    trigger,
    result,
    t,
  };
}

const styles = {
  popover: css`
    .ant-popover-arrow {
      display: none;
    }
    .ant-popover-inner-content {
      display: flex;
      align-items: center;
      padding: 4px;
      > div {
        width: 24px;
        height: 24px;
        line-height: 24px;
        text-align: center;
        font-size: 16px;
        cursor: pointer;
        &:hover {
          background: var(--fett-bg-app);
        }
      }
    }
  `,
};

export default useModeTranslate;
